import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, Download, BarChart3, TrendingUp, Globe, 
  AlertTriangle, Clock, Monitor, Smartphone, Tablet,
  ChevronDown, Filter, Calendar, FileText, MapPin,
  Activity, Zap, Eye, Users, Target, Lock
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DevToolsProtection from './DevToolsProtection';
import './SecurityDashboard.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement);

// Mock data generators
const generateMockSecurityData = () => {
  const attackTypes = ['Phishing', 'Malware', 'DDoS', 'SQL Injection', 'Brute Force', 'XSS'];
  const countries = ['Russia', 'China', 'North Korea', 'Iran', 'Brazil', 'India', 'Unknown'];
  const data = [];
  
  for (let i = 0; i < 50; i++) {
    data.push({
      id: i + 1,
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      eventType: attackTypes[Math.floor(Math.random() * attackTypes.length)],
      sourceIP: `${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}`,
      destination: 'honeypot.itlogan.com',
      threatLevel: ['Low', 'Medium', 'High', 'Critical'][Math.floor(Math.random() * 4)],
      country: countries[Math.floor(Math.random() * countries.length)],
      blocked: Math.random() > 0.3,
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
    });
  }
  return data;
};

const generateGeoData = () => {
  return [
    { lat: 55.7558, lng: 37.6176, country: 'Russia', attacks: 145, severity: 'high' },
    { lat: 39.9042, lng: 116.4074, country: 'China', attacks: 123, severity: 'high' },
    { lat: 39.0392, lng: 125.7625, country: 'North Korea', attacks: 89, severity: 'critical' },
    { lat: 35.6892, lng: 51.3890, country: 'Iran', attacks: 67, severity: 'medium' },
    { lat: -14.2350, lng: -51.9253, country: 'Brazil', attacks: 45, severity: 'medium' },
    { lat: 20.5937, lng: 78.9629, country: 'India', attacks: 34, severity: 'low' },
    { lat: 52.5200, lng: 13.4050, country: 'Germany', attacks: 23, severity: 'low' },
    { lat: 48.8566, lng: 2.3522, country: 'France', attacks: 19, severity: 'low' }
  ];
};

function SecurityDashboard() {
  const [mockData] = useState(generateMockSecurityData());
  const [geoData] = useState(generateGeoData());
  const [timeRange, setTimeRange] = useState('24h');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState('overview');
  const [currentTime, setCurrentTime] = useState(new Date());
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const dropdownRef = useRef(null);

  // Responsive breakpoint detection
  const [screenSize, setScreenSize] = useState('desktop');
  
  useEffect(() => {
    const updateScreenSize = () => {
      if (window.innerWidth < 768) setScreenSize('mobile');
      else if (window.innerWidth < 1024) setScreenSize('tablet');
      else setScreenSize('desktop');
    };
    
    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Live Clock useEffect
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  // Initialize world map
  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      mapInstance.current = L.map(mapRef.current, {
        center: [20, 0],
        zoom: 2,
        zoomControl: true,
        scrollWheelZoom: false
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstance.current);

      // Add attack markers
      geoData.forEach(point => {
        const color = point.severity === 'critical' ? '#dc2626' : 
                     point.severity === 'high' ? '#ea580c' :
                     point.severity === 'medium' ? '#ca8a04' : '#16a34a';
        
        const marker = L.circleMarker([point.lat, point.lng], {
          radius: Math.min(point.attacks / 10, 15),
          fillColor: color,
          color: color,
          weight: 2,
          opacity: 0.8,
          fillOpacity: 0.6
        }).addTo(mapInstance.current);

        marker.bindPopup(`
          <div class="map-popup">
            <h3>${point.country}</h3>
            <p>Attacks: ${point.attacks}</p>
            <p>Severity: ${point.severity}</p>
          </div>
        `);
      });
    }

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [geoData]);

  // Chart data
  const attackTypeData = {
    labels: ['Phishing', 'Malware', 'DDoS', 'SQL Injection', 'Brute Force', 'XSS'],
    datasets: [{
      data: [30, 25, 20, 12, 8, 5],
      backgroundColor: [
        '#0e38b1', '#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'
      ],
      borderWidth: 0
    }]
  };

  const trendData = {
    labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
    datasets: [{
      label: 'Attack Volume',
      data: [12, 19, 25, 42, 28, 35],
      borderColor: '#0e38b1',
      backgroundColor: 'rgba(14, 56, 177, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const countryData = {
    labels: ['Russia', 'China', 'North Korea', 'Iran', 'Brazil'],
    datasets: [{
      label: 'Attacks',
      data: [145, 123, 89, 67, 45],
      backgroundColor: '#0e38b1',
      borderRadius: 4
    }]
  };

  // Export functions
  const exportCSV = () => {
    const headers = ['Timestamp', 'Event Type', 'Source IP', 'Destination', 'Threat Level', 'Country', 'Blocked'];
    const csvContent = [
      headers.join(','),
      ...mockData.map(row => [
        row.timestamp,
        row.eventType,
        row.sourceIP,
        row.destination,
        row.threatLevel,
        row.country,
        row.blocked
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `honeypot_security_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('Honeypot Security Center Report', 20, 30);
    
    doc.setFontSize(12);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 45);
    doc.text('Time Range: Last 24 Hours', 20, 55);
    
    // Summary stats
    doc.setFontSize(16);
    doc.text('Security Summary', 20, 75);
    
    doc.setFontSize(12);
    doc.text(`Total Events: ${mockData.length}`, 20, 90);
    doc.text(`Blocked Attacks: ${mockData.filter(d => d.blocked).length}`, 20, 100);
    doc.text(`Critical Threats: ${mockData.filter(d => d.threatLevel === 'Critical').length}`, 20, 110);
    
    // Table of recent events
    const tableData = mockData.slice(0, 10).map(row => [
      new Date(row.timestamp).toLocaleString(),
      row.eventType,
      row.sourceIP,
      row.threatLevel,
      row.blocked ? 'Yes' : 'No'
    ]);
    
    autoTable(doc, {
      head: [['Timestamp', 'Event Type', 'Source IP', 'Threat Level', 'Blocked']],
      body: tableData,
      startY: 125,
      styles: { fontSize: 8 }
    });
    
    doc.save(`honeypot_security_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Simulated intrusion detection
  const simulateIntrusionDetection = () => {
    const mockAttackerData = {
      ip: '192.168.1.100',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      attemptedPage: '/admin',
      location: 'Unknown Location'
    };

    // Visual notification
    alert(`🚨 SECURITY ALERT 🚨\n\nUnauthorized access attempt detected!\n\nIP: ${mockAttackerData.ip}\nTime: ${mockAttackerData.timestamp}\nAttempted Access: ${mockAttackerData.attemptedPage}\n\n⚠️ NOTE: In a real implementation, complete logs would be automatically sent to:\n• rloganjr1025@gmail.com\n• absons.ramon@gmail.com\n\nThis is a demonstration - actual email sending requires backend infrastructure.`);
    
    console.log('🔒 SECURITY LOG:', mockAttackerData);
  };

  const getResponsiveColumns = () => {
    switch (screenSize) {
      case 'mobile': return 'grid-cols-1';
      case 'tablet': return 'grid-cols-2';
      default: return 'grid-cols-3';
    }
  };

  return (
    <DevToolsProtection adminKey="itlogan_admin_2025_secure">
      <div className="min-h-screen bg-[#0d0d0d] text-white">
      
      {/* Floating Time Range Selector - Above Header */}
      <div className="fixed top-4 right-4 z-50 flex items-center space-x-3" ref={dropdownRef}>
        {/* Mobile Clock Display */}
        <div className="lg:hidden px-3 py-2 rounded-lg text-xs text-gray-300"
             style={{
               background: 'linear-gradient(135deg, rgba(13, 13, 13, 0.9), rgba(17, 24, 39, 0.9))',
               backdropFilter: 'blur(20px)',
               border: '1px solid rgba(255, 255, 255, 0.1)',
               boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
             }}>
          {currentTime.toLocaleDateString('en-US', { 
            month: 'short',
            day: 'numeric'
          })} • {currentTime.toLocaleTimeString('en-US', { 
            hour12: false,
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>

      {/* Header */}
      <header className="glassmorphism-header p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-[#0e38b1]" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Honeypot Security Center</h1>
              <p className="text-gray-400 text-sm">Real-time Threat Intelligence Dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Live Clock Display - Desktop */}
            <div className="hidden lg:flex items-center space-x-2 px-3 py-2 rounded-lg"
                 style={{
                   background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
                   backdropFilter: 'blur(20px)',
                   border: '1px solid rgba(255, 255, 255, 0.15)'
                 }}>
              <Clock className="w-4 h-4 text-gray-400" />
              <div className="text-sm text-gray-300">
                {currentTime.toLocaleDateString('en-US', { 
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })} • {currentTime.toLocaleTimeString('en-US', { 
                  hour12: false,
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Export Buttons */}
              <button
                onClick={exportCSV}
                className="glassmorphism-button px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export CSV</span>
              </button>
              
              <button
                onClick={generatePDF}
                className="glassmorphism-button px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Generate PDF</span>
              </button>

              {/* Simulate Intrusion Button */}
              <button
                onClick={simulateIntrusionDetection}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <AlertTriangle className="h-4 w-4" />
                <span className="hidden sm:inline">Simulate Attack</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 md:p-6 relative">
        {/* Overview Stats */}
        <div className={`grid ${getResponsiveColumns()} gap-4 md:gap-6 mb-6`}>
          <div className="glassmorphism-card p-6 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Threats</p>
                <p className="text-3xl font-bold text-[#0e38b1]">{mockData.length}</p>
              </div>
              <Target className="h-12 w-12 text-[#0e38b1] opacity-80" />
            </div>
          </div>
          
          <div className="glassmorphism-card p-6 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Blocked</p>
                <p className="text-3xl font-bold text-green-400">{mockData.filter(d => d.blocked).length}</p>
              </div>
              <Shield className="h-12 w-12 text-green-400 opacity-80" />
            </div>
          </div>
          
          <div className="glassmorphism-card p-6 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Critical</p>
                <p className="text-3xl font-bold text-red-400">{mockData.filter(d => d.threatLevel === 'Critical').length}</p>
              </div>
              <AlertTriangle className="h-12 w-12 text-red-400 opacity-80" />
            </div>
          </div>
        </div>

        {/* Geographic Intelligence Section */}
        <div className="glassmorphism-card p-6 rounded-xl mb-6">
          <div className="flex items-center space-x-3 mb-6">
            <Globe className="h-6 w-6 text-[#0e38b1]" />
            <h2 className="text-xl md:text-2xl font-bold">Geographic Intelligence</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div 
                ref={mapRef} 
                className="h-64 md:h-80 w-full rounded-lg bg-gray-800"
                style={{ minHeight: '300px' }}
              />
            </div>
            
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Top Attack Origins</h3>
              {geoData.slice(0, 5).map((country, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-4 w-4 text-[#0e38b1]" />
                    <span>{country.country}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{country.attacks}</div>
                    <div className={`text-xs ${
                      country.severity === 'critical' ? 'text-red-400' :
                      country.severity === 'high' ? 'text-orange-400' :
                      country.severity === 'medium' ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {country.severity}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Attack Analysis Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Attack Types Distribution */}
          <div className="glassmorphism-card p-6 rounded-xl">
            <div className="flex items-center space-x-3 mb-6">
              <BarChart3 className="h-6 w-6 text-[#0e38b1]" />
              <h2 className="text-xl font-bold">Attack Types Distribution</h2>
            </div>
            <div className="h-64">
              <Pie 
                data={attackTypeData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { color: 'white', padding: 20 }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Attack Trends */}
          <div className="glassmorphism-card p-6 rounded-xl">
            <div className="flex items-center space-x-3 mb-6">
              <TrendingUp className="h-6 w-6 text-[#0e38b1]" />
              <h2 className="text-xl font-bold">Attack Trends (24h)</h2>
            </div>
            <div className="h-64">
              <Line 
                data={trendData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: { color: 'rgba(255,255,255,0.1)' },
                      ticks: { color: 'white' }
                    },
                    x: {
                      grid: { color: 'rgba(255,255,255,0.1)' },
                      ticks: { color: 'white' }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Top Attacking Countries */}
        <div className="glassmorphism-card p-6 rounded-xl mb-6">
          <div className="flex items-center space-x-3 mb-6">
            <Activity className="h-6 w-6 text-[#0e38b1]" />
            <h2 className="text-xl font-bold">Top Attacking Countries</h2>
          </div>
          <div className="h-64">
            <Bar 
              data={countryData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: { color: 'rgba(255,255,255,0.1)' },
                    ticks: { color: 'white' }
                  },
                  x: {
                    grid: { display: false },
                    ticks: { color: 'white' }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Recent Security Events */}
        <div className="glassmorphism-card p-6 rounded-xl">
          <div className="flex items-center space-x-3 mb-6">
            <Eye className="h-6 w-6 text-[#0e38b1]" />
            <h2 className="text-xl font-bold">Recent Security Events</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Event Type</th>
                  <th className="text-left p-3">Source IP</th>
                  <th className="text-left p-3">Threat Level</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {mockData.slice(0, 10).map((event, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">{new Date(event.timestamp).toLocaleTimeString()}</td>
                    <td className="p-3">{event.eventType}</td>
                    <td className="p-3 font-mono">{event.sourceIP}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        event.threatLevel === 'Critical' ? 'bg-red-500/20 text-red-300' :
                        event.threatLevel === 'High' ? 'bg-orange-500/20 text-orange-300' :
                        event.threatLevel === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-green-500/20 text-green-300'
                      }`}>
                        {event.threatLevel}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        event.blocked ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
                      }`}>
                        {event.blocked ? 'Blocked' : 'Allowed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 px-6">
        <div className="text-sm text-gray-400">
          <p className="mb-2">
            ⚠️ <strong>Security Notice:</strong> Intrusion detection is active. Unauthorized access attempts are logged and reported.
          </p>
          <p className="font-medium">
            Ramon Logan Jr. © 2025 IT Logan. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
    </DevToolsProtection>
  );
}

export default SecurityDashboard;
